// Phase 40 Plan 02 Task 1 — /auth/callback handler.
// CONTEXT D-12. UI-SPEC §2. RESEARCH.md Pattern 2.
//
// Mounted at /auth/callback by App.jsx (early-return route guard). On mount
// we exchange the OAuth `?code=…` query param for a Supabase session, then
// redirect to /. The AuthProvider's onAuthStateChange handler picks up the
// SIGNED_IN event and triggers the first-sign-in upload via Plan 02 Task 4
// wiring.
//
// On error we redirect to /?auth_error=… and rely on App.jsx to surface the
// fallback notification. The static fallback at public/auth/callback.html
// handles the same flow when the React bundle is unavailable (universal-link
// `tend://auth/callback` target — Phase 42 deep-link).

import React, { useEffect, useState } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';
import { supabase } from './supabaseClient.js';

export default function AuthCallback() {
  const [errored, setErrored] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);

  useEffect(() => {
    const url = new URL(window.location.href);
    const code = url.searchParams.get('code');
    const queryError = url.searchParams.get('error') || url.searchParams.get('error_description');
    const hashStr = window.location.hash || '';
    const hashHasError = hashStr.includes('error=') || hashStr.includes('error_description=');
    const hashHasToken = hashStr.includes('access_token=');
    const homeUrl = `${import.meta.env.BASE_URL || '/'}`;
    const homeBase = homeUrl.endsWith('/') ? homeUrl.slice(0, -1) : homeUrl;
    let cancelled = false;
    const finalErrInline = (msg) => {
      if (cancelled) return;
      setErrored(msg);
      setTimeout(() => {
        if (!cancelled) {
          window.location.replace(`${homeBase}/?auth_error=` + encodeURIComponent(msg));
        }
      }, 1500);
    };

    // eslint-disable-next-line no-console
    console.info('[AuthCallback]', {
      pathname: url.pathname,
      hasCode: !!code,
      hasFragment: !!hashStr,
      hashHasToken,
      hashHasError,
      queryError,
      hashPreview: hashStr.slice(0, 120),
    });

    // Surface provider-side errors (Facebook/Apple/Google sometimes return
    // ?error=… or #error=… instead of a token — e.g. user cancelled, scope
    // denied, app misconfigured).
    if (queryError || hashHasError) {
      const m = queryError
        || (() => {
          try {
            const p = new URLSearchParams(hashStr.replace(/^#/, ''));
            return p.get('error_description') || p.get('error') || 'provider returned an error';
          } catch { return 'provider returned an error'; }
        })();
      finalErrInline(m);
      return () => { cancelled = true; };
    }

    if (!code && !hashHasToken) {
      // No code AND no token AND no error — likely a direct visit. Send home.
      window.location.replace(homeUrl);
      return;
    }

    // PKCE leftover (older Supabase email templates may still emit ?code=…).
    if (code) {
      supabase.auth.exchangeCodeForSession(code).then(({ error }) => {
        if (error) {
          finalErrInline(error.message || 'sign-in failed');
          return;
        }
        if (!cancelled) window.location.replace(homeUrl);
      }).catch((e) => finalErrInline(e?.message || 'sign-in failed'));
      return () => { cancelled = true; };
    }

    // Implicit flow — detectSessionInUrl: true parses the URL fragment in
    // the SDK constructor. Check getSession() FIRST; fall back to
    // onAuthStateChange + INITIAL_SESSION for the race-after-mount path.
    let resolved = false;
    const goHome = () => {
      if (cancelled || resolved) return;
      resolved = true;
      window.location.replace(homeUrl);
    };

    supabase.auth.getSession().then(({ data, error }) => {
      // eslint-disable-next-line no-console
      console.info('[AuthCallback] getSession', { hasSession: !!data?.session, error: error?.message });
      if (data?.session) goHome();
    }).catch((e) => {
      // eslint-disable-next-line no-console
      console.warn('[AuthCallback] getSession threw', e);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      // eslint-disable-next-line no-console
      console.info('[AuthCallback] onAuthStateChange', { event, hasSession: !!session });
      if (resolved || cancelled) return;
      if (event === 'SIGNED_IN' || (event === 'INITIAL_SESSION' && session)) {
        goHome();
      }
    });

    // 2s diagnostic capture: if no resolution, snapshot what we observed and
    // surface it inline so the user sees exact symptoms (was hash empty? did
    // getSession return null?). Then 1.5s later commit to the auth_error redirect.
    const captureTimer = setTimeout(async () => {
      if (resolved || cancelled) return;
      let sessionSnapshot = null;
      try {
        const { data } = await supabase.auth.getSession();
        sessionSnapshot = !!data?.session;
      } catch {}
      const snapshot = {
        hashLength: hashStr.length,
        hashHasToken,
        sessionAfter2s: sessionSnapshot,
        localStorageKeys: Object.keys(localStorage).filter((k) => k.startsWith('sb-')),
      };
      // eslint-disable-next-line no-console
      console.warn('[AuthCallback] no resolution after 2s', snapshot);
      setDebugInfo(snapshot);
      finalErrInline('Sign-in did not complete — provider returned no session token. See console for details.');
    }, 2000);

    return () => {
      cancelled = true;
      clearTimeout(captureTimer);
      sub?.subscription?.unsubscribe?.();
    };
  }, []);

  // Visual: full-page splash on --c-page background. Spinner respects
  // prefers-reduced-motion via CSS media query (no JS detection needed —
  // browsers degrade `animation` automatically with the right rule, but
  // for a safety net we accept the spinner stays static if the user's OS
  // prefers reduced motion).
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--c-page)',
        color: 'var(--c-text)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 16,
        padding: 24,
        textAlign: 'center',
        fontFamily: 'Lora, serif',
      }}
    >
      {errored ? (
        <>
          <AlertCircle size={48} stroke="var(--c-danger)" aria-hidden="true" />
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'var(--font-h2)', color: 'var(--c-heading)', margin: 0, fontWeight: 700 }}>
            Couldn't complete sign-in.
          </h2>
          <p style={{ fontSize: '0.9375rem', color: 'var(--c-text-2)', margin: 0, maxWidth: 360, lineHeight: 1.55 }}>
            {errored}
          </p>
          {debugInfo && (
            <pre style={{
              background: 'var(--c-surface)', color: 'var(--c-text-2)',
              border: '1px solid var(--c-border)', borderRadius: 8,
              padding: 12, fontSize: '0.6875rem', lineHeight: 1.4, maxWidth: 480,
              overflowX: 'auto', textAlign: 'left',
            }}>
{JSON.stringify(debugInfo, null, 2)}
            </pre>
          )}
          <button
            type="button"
            onClick={() => window.location.replace(`${import.meta.env.BASE_URL || '/'}`)}
            style={{
              background: 'var(--c-brand-deep)', color: '#fff', border: 'none',
              borderRadius: 24, padding: '12px 28px', fontSize: '0.9375rem', fontWeight: 600,
              cursor: 'pointer', minHeight: 44, fontFamily: 'inherit',
            }}
          >
            Back to Tend
          </button>
        </>
      ) : (
        <>
          <Loader2
            size={48}
            stroke="var(--c-brand-deep)"
            aria-hidden="true"
            style={{ animation: 'tendSpin 1s linear infinite' }}
          />
          <p style={{ fontSize: '0.9375rem', color: 'var(--c-text-2)', margin: 0 }}>
            Signing you in…
          </p>
          <style>{`
            @keyframes tendSpin { to { transform: rotate(360deg); } }
            @media (prefers-reduced-motion: reduce) {
              [data-tend-auth-spinner] { animation: none !important; }
            }
          `}</style>
        </>
      )}
    </div>
  );
}
